const ALLOWED_CSV_MIME_TYPES = new Set([
  "",
  "text/csv",
  "application/csv",
  "application/x-csv",
  "text/plain",
  "application/vnd.ms-excel",
]);

export function isSupportedCsvFile(file: {
  name: string;
  type: string;
}): boolean {
  const name = file.name.trim();
  const extensionStart = name.lastIndexOf(".");
  if (extensionStart < 0) {
    return false;
  }
  const extension = name.slice(extensionStart).toLowerCase();
  if (extension !== ".csv") {
    return false;
  }
  const mime = file.type.split(";")[0].trim().toLowerCase();
  return ALLOWED_CSV_MIME_TYPES.has(mime);
}

export function readFileAsText(file: Blob): Promise<string> {
  if (typeof file.text === "function") {
    return file.text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () =>
      reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsText(file);
  });
}

export function isSupportedCsvContent(text: string): boolean {
  if (text.includes("\u0000")) {
    return false;
  }
  return !(
    text.length >= 4 &&
    text.charCodeAt(0) === 0x50 &&
    text.charCodeAt(1) === 0x4b &&
    (text.charCodeAt(2) === 0x03 ||
      text.charCodeAt(2) === 0x05 ||
      text.charCodeAt(2) === 0x07)
  );
}

export function parseCsvSeeds(text: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  const pushCell = () => {
    const value = current.trim();
    current = "";
    if (value) {
      cells.push(value);
    }
  };

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
        continue;
      }
      current += char;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    if (char === ",") {
      pushCell();
      continue;
    }

    if (char === "\n" || char === "\r") {
      pushCell();
      if (char === "\r" && text[i + 1] === "\n") {
        i += 1;
      }
      continue;
    }

    current += char;
  }

  pushCell();
  return normalizeSeeds(cells);
}

/** Trim and drop empty or case-insensitive duplicate seeds, keeping first casing. */
export function normalizeSeeds(seeds: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  seeds.forEach((seed) => {
    const trimmed = seed.trim();
    if (!trimmed) {
      return;
    }
    const key = trimmed.toLocaleLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(trimmed);
    }
  });
  return unique;
}

export function mergeUniqueSeeds(
  existing: string[],
  incoming: string[]
): string[] {
  return normalizeSeeds([...existing, ...incoming]);
}

export function parseTypedSeeds(text: string): string[] {
  return normalizeSeeds(text.split(","));
}

function stringList(value: unknown): string[] | null {
  if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
    return value;
  }
  return null;
}

export function normalizeTopics(topics: string[]): string[] {
  return topics.map((topic) => topic.trim()).filter(Boolean);
}

export function topicsFromModelResponse(data: unknown): string[] {
  const fromArray = stringList(data);
  if (fromArray) {
    return normalizeTopics(fromArray);
  }
  if (data && typeof data === "object") {
    const record = data as { topic?: unknown; topics?: unknown };
    const fromTopic = stringList(record.topic) ?? stringList(record.topics);
    if (fromTopic) {
      return normalizeTopics(fromTopic);
    }
  }
  throw new Error("Topic model returned an unexpected response");
}

export function seedsMatch(left: string[], right: string[]): boolean {
  const normalizedLeft = normalizeSeeds(left);
  const normalizedRight = normalizeSeeds(right);
  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }
  return normalizedLeft.every(
    (seed, index) =>
      seed.toLocaleLowerCase() === normalizedRight[index].toLocaleLowerCase()
  );
}

/** True only when seeds changed after the last generate. Name edits do not count. */
export function needsTopicRegenerate(
  seeds: string[],
  topics: string[],
  generatedFromSeeds: string[] | null
): boolean {
  if (topics.length === 0) {
    return false;
  }
  if (generatedFromSeeds === null) {
    return true;
  }
  return !seedsMatch(seeds, generatedFromSeeds);
}
